import { css } from "@compiled/react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { media } from "@atlaskit/primitives/responsive";

import {
    templateBenefitsRelayFragment$data,
    templateBenefitsRelayFragment$key
} from "@/__generated__/templateBenefitsRelayFragment.graphql";
import { SanitizedRichText } from "@/components/confluence/util/sanitized-rich-text";

type TemplateBenefitsProps = {
    templateBenefits: templateBenefitsRelayFragment$key;
};

const tabbedSectionStyle = css({
    "marginBlockStart": 56,
    "marginInlineStart": "auto",
    "marginInlineEnd": "auto",

    "@media (max-width: 39.99rem)": {
        maxWidth: "100%"
    }
});

const stepContainerStyle = css({
    "display": "flex",
    "justifyContent": "space-between",
    "paddingBlockEnd": "3rem",
    "marginBlockEnd": "3rem",
    "borderBlockEnd": "2px solid #ebecf0",
    "gap": "2rem",

    "@media (max-width: 39.99rem)": {
        flexDirection: "column"
    },

    ":last-of-type": {
        borderBlockEnd: "none"
    }
});

const tabbedSectionHeading = css({
    "fontSize": "2.25rem",
    "lineHeight": "2.75rem",
    "fontWeight": 500,
    "letterSpacing": 0.3,
    "color": "#1d3059",
    "fontFamily": "var(--bxp-charlie-display-font-family)",
    "margin": 0,
    "marginBlockEnd": 40,

    "@media (max-width: 47.99rem)": {
        fontSize: "1.5rem",
        lineHeight: "1.75rem"
    }
});

const stepHeading = css({
    fontSize: "1.5rem",
    lineHeight: "2rem",
    fontWeight: 500,
    letterSpacing: 0.3,
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#1d3059",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    fontFamily: "var(--bxp-charlie-display-font-family)",
    margin: 0,

    [media.below.sm]: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem"
    }
});

const stepContentWrapper = css({
    "display": "flex",
    "flexDirection": "column",
    "gap": "2rem",

    "@media (max-width: 39.99rem)": {
        gap: "1.5rem"
    }
});

const TemplateBenefitsStep = ({
    name,
    shortDescription
}: NonNullable<
    templateBenefitsRelayFragment$data["templateBenefitContainer"]
>[number]) => {
    return (
        <div css={stepContainerStyle}>
            <div css={stepContentWrapper}>
                <h3 css={stepHeading}>{name}</h3>
                {shortDescription && (
                    <SanitizedRichText innerHTML={shortDescription} />
                )}
            </div>
        </div>
    );
};

const TemplateBenefitsRelayFragment = graphql`
    fragment templateBenefitsRelayFragment on ContentPlatformTemplateBenefitContainer {
        benefitsTitle
        templateBenefitContainer {
            name
            shortDescription
        }
    }
`;

export const TemplateBenefits = ({
    templateBenefits
}: TemplateBenefitsProps) => {
    const { benefitsTitle, templateBenefitContainer } = useFragment(
        TemplateBenefitsRelayFragment,
        templateBenefits
    );
    return (
        <section css={tabbedSectionStyle}>
            <h2 css={tabbedSectionHeading}>{benefitsTitle}</h2>
            {templateBenefitContainer?.map((benefitDetail, index: number) => (
                <TemplateBenefitsStep
                    key={`benefit-${index}`}
                    name={benefitDetail.name}
                    shortDescription={benefitDetail.shortDescription}
                />
            ))}
        </section>
    );
};
